import type { ReactNode } from 'react';
import type { IconName } from '../../icons/Icon';

export interface Command {
  id: string;
  label: string;
  /** Group heading in the results list: Recent / Navigation / Actions / Entities / Help, or a source's own. */
  group?: string;
  /** Extra search terms. */
  keywords?: string[];
  icon?: IconName | ReactNode;
  /** Shortcut spec rendered with Kbd, e.g. "mod+shift+P". */
  shortcut?: string;
  /** Secondary line under the title. */
  description?: string;
  /** A status code (e.g. `IN_TRANSIT`), rendered through the palette's `renderStatus` so the palette never imports the status registry. */
  status?: string;
  /** The typed prefix this command answers to (`s`, `o`, `w`, `sku`); commands without one hide while a prefix is active. */
  scope?: string;
  disabled?: boolean;
  /**
   * In-app destination of a navigation command. Records found by a {@link CommandSource} set it so the palette can
   * offer them again under "Recent" without re-running the search (the palette calls `onNavigate(href)`).
   */
  href?: string;
  onRun: () => void | Promise<void>;
}

/**
 * A provider of search results: record lookups (orders, SKUs, LPNs…), context switches, or — later — an AI
 * assistant. Sources are queried in parallel after a short debounce; each receives an `AbortSignal` that fires
 * as soon as the query changes or the palette closes, so a slow source never overwrites newer results.
 */
export interface CommandSource {
  /** Stable id (used for per-source loading/error state). */
  id: string;
  /** Group heading for results that carry no `group` of their own, and the name used in failure notices. */
  label: string;
  /** Added to every result's match score: higher ranks list earlier among equally good matches (default 0). */
  rank?: number;
  /** Minimum trimmed query length before the source is asked (default 2). */
  minQueryLength?: number;
  /**
   * The typed prefix that narrows the palette to this source: `s` answers `s:SHP-1`, `sku` answers `sku:ABC`.
   * While a prefix is typed only the sources carrying it are asked.
   */
  scope?: string;
  search: (query: string, signal: AbortSignal) => Promise<Command[]>;
}

/** Every user-visible string of the palette, so the host can localize it. */
export interface CommandPaletteMessages {
  dialogLabel: string;
  listLabel: string;
  searching: string;
  recent: string;
  navigate: string;
  run: string;
  close: string;
  /** Footer hint for Tab (shown when `onPeek` is set). */
  peek: string;
  /** Footer hint listing the typed prefixes, e.g. "s: o: w: sku:". Receives the active scopes. */
  prefixes: (scopes: string[]) => string;
  /** Notice shown when one source failed; the rest of the results stay usable. */
  sourceFailed: (sourceLabel: string) => string;
  /** Screen-reader announcement of the result count. */
  results: (count: number) => string;
}

export const defaultCommandPaletteMessages: CommandPaletteMessages = {
  dialogLabel: 'Command palette',
  listLabel: 'Commands',
  searching: 'Searching…',
  recent: 'Recent',
  navigate: 'navigate',
  run: 'run',
  close: 'close',
  peek: 'peek',
  prefixes: (scopes) => scopes.map((s) => `${s}:`).join(' '),
  sourceFailed: (label) => `${label} could not be searched right now.`,
  results: (count) => (count === 1 ? '1 result' : `${count} results`),
};

/** The canonical section order of the palette. */
export const DEFAULT_COMMAND_GROUP_ORDER = ['Recent', 'Navigation', 'Actions', 'Entities', 'Help'];

/** Splits `s:SHP-1` into `{ scope: 's', rest: 'SHP-1' }` when `s` is a known scope; otherwise the whole query is the rest. */
export function parseScopedQuery(query: string, scopes: ReadonlySet<string>): { scope: string | null; rest: string } {
  const match = /^([a-z][a-z0-9-]*):\s*(.*)$/i.exec(query.trim());
  if (match) {
    const scope = match[1]!.toLowerCase();
    if (scopes.has(scope)) return { scope, rest: match[2] ?? '' };
  }
  return { scope: null, rest: query };
}
